// The pre-printed Tamil Nadu non-judicial stamp paper header, served from our own `public/`.
//
// This used to be a URL on a third-party CDN left over from the original scaffolding tool. That
// made a core piece of the UI — the sheet every document preview is drawn on — depend on an
// external host we do not control, which would have broken silently the day that host stopped
// serving it. Vendored locally so the app has no runtime dependency on anyone else's infra.
export const STAMP_IMG = "/stamp-paper.jpg";

// Real A4, so what's on screen reads as the sheet it will actually be printed on.
// 210mm x 297mm at 96dpi; the ratio is what matters, the pixel width is just a comfortable
// on-screen size. Shared by the wizard preview, the customer's document view and the admin
// editor so the three can never drift apart.
export const A4_W = 794;
export const A4_H = 1123;               // 794 * 297/210
export const STAMP_HEADER_H = 300;      // the pre-printed band at the top of the sheet
export const A4_BODY_H = A4_H - STAMP_HEADER_H;

// The stamp image must bleed to all four edges. `contain` letterboxed it against the sheet's
// background, which showed as white corners and instantly read as a picture pasted onto a page
// rather than the paper itself. `cover` + top anchoring fills the band and crops the overhang.
export const stampImgStyle = { height: STAMP_HEADER_H, objectFit: "cover", objectPosition: "top center" };

// Typography of the printed body — shared by DocumentSheet's real sheets and its own hidden
// measurement host (they must match, or pagination measures something other than what renders),
// and by the Template Catalog's inline preview of a body being edited.
const FONT_FAMILY = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
const BODY_FONT_SIZE = 13;
const BODY_LINE_HEIGHT = 1.85;
export const BODY_CSS = {
  fontFamily: FONT_FAMILY, fontSize: BODY_FONT_SIZE, lineHeight: BODY_LINE_HEIGHT, fontWeight: 400,
  textAlign: "left", whiteSpace: "pre-wrap", wordBreak: "break-word",
  // Before the body was split into one <div> per block (for per-heading styling), the whole
  // document rendered as a single pre-wrap string, so a blank line in the source *was* a blank
  // line on screen — "\n\n" preserved literally. Splitting into blocks throws that away unless
  // something puts the gap back.
  //
  // **A full line's height, not a token few pixels.** DocumentSheet displays a whole A4 page
  // scaled down to fit a small preview pane — often to under half size — and a CSS transform
  // scales margins exactly as it scales text, so a modest flat value (10px was tried first)
  // shrinks to 3-4 *screen* pixels: technically present, invisible next to ordinary line spacing.
  // Sized to one full line instead, a paragraph break now looks like the blank line an admin
  // actually typed, at any scale — verified against the real running preview, not assumed: a
  // Playwright probe measured "AAA\nBBB" (one block, an internal line break) at exactly 2x the
  // height of a single-line block, so the line break itself was always rendering correctly; only
  // the *paragraph-to-paragraph* gap was too small to see.
  marginBottom: Math.round(BODY_FONT_SIZE * BODY_LINE_HEIGHT),
};
// `<h1>` through `<h6>` — h1 is "+2, bold, centred" (the one explicitly asked for, for a
// document's own title); h2-h6 step down in size and stay left-aligned, the same way a real
// heading hierarchy reads. Mirrors core.pdf's `_HEADING_PT_OVER_BODY` ladder exactly.
const _HEADING_PT_OVER_BODY = { 1: 2, 2: 1.5, 3: 1, 4: 0.5, 5: 0, 6: 0 };
export const HEADING_CSS = Object.fromEntries(
  Object.entries(_HEADING_PT_OVER_BODY).map(([lvl, delta]) => [lvl, {
    ...BODY_CSS, fontSize: BODY_CSS.fontSize + delta, fontWeight: 700, marginBottom: Number(lvl) === 1 ? 10 : 6,
    textAlign: Number(lvl) === 1 ? "center" : "left",
  }])
);

/** A block's style — kept as one function so a heading can never look different in two places.
 *  `align` (from an explicit `align="left|center|right"` on the wrapping tag — see splitBlocks)
 *  overrides the kind's own default alignment; `undefined`/falsy leaves it alone. Mirrors
 *  core.pdf's `_ALIGN_TO_TA` override-via-clone. */
export const blockStyle = (kind, align) => {
  const m = /^h([1-6])$/.exec(kind || "");
  const base = m ? HEADING_CSS[m[1]] : BODY_CSS;
  return align ? { ...base, textAlign: align } : base;
};

// Classic HTML's `<font size="1">`..`<font size="7">` scale, as a ratio of whatever body size is
// already in effect — mirrors core.pdf._FONT_SIZE_RATIO exactly. This is also how "bold at a
// bigger size" is expressed in real HTML: `<font size="5"><b>...</b></font>`, not an invented tag.
export const FONT_SIZE_RATIO = { 1: 0.67, 2: 0.83, 3: 1.0, 4: 1.17, 5: 1.5, 6: 2.0, 7: 3.0 };

// An unfilled field must read as a blank to be filled in — never as a plausible value. Showing
// "residential" or "11" or "30" for something the customer hasn't answered makes the live preview
// look like a completed document and invites them to accept a term they never chose.
export const BLANK = "____________";

// Dates are stored ISO (YYYY-MM-DD — what <input type="date"> requires and what sorts correctly)
// and printed Indian (DD-MM-YYYY). Mirrors core.templates_data.format_date_for_document; keep the
// two in step or the live preview and the printed PDF will disagree about the date.
export const DATE_SEP = "-";
export const formatDocDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v).trim());
  return m ? [m[3], m[2], m[1]].join(DATE_SEP) : String(v);
};

/**
 * Substitute {{field}} tokens in a template body with the transaction's data.
 *
 * The single place the *template* becomes readable text on the client, so the wizard preview
 * shows the same document the PDF will contain instead of a hand-written copy of it. Mirrors the
 * server-side substitution in core.templates_data.render_body — including wrapping every filled
 * answer `<b>...</b>`, so a customer's own words stand out from the boilerplate in the live
 * preview exactly as they will on the printed page.
 *
 * Every token is replaced — one whose field is absent or blank becomes a fill-in rule, never a
 * leftover `{{purpose}}`.
 *
 * Unlike the server, this runs on the customer's own **unsaved, in-progress** input as they type
 * — it has never been through `core.sanitize.sanitize()`. So `<`/`>`/`&` in a typed value are
 * escaped right here, before wrapping, rather than assumed safe: `renderInline` below treats its
 * input as already-safe markup plus our own `<b>` tags, and a stray `<` typed into an address
 * field must render as the character the customer typed, not vanish into a tag `renderInline`
 * tries to parse.
 */
//
// Only a *bare* `&` is escaped. A saved answer has already been through sanitize(), which wrote a
// typed `<` as `&lt;`; escaping that again made the preview read "&lt;". Mirrors the server's
// core.sanitize.escape_bare_amp.
const _BARE_AMP_RE = /&(?!(?:amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-fA-F]+);)/g;
const _escapeForInlineMarkup = (s) =>
  String(s).replace(_BARE_AMP_RE, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Entities → the characters they stand for. The stored body keeps a literal `&` as `&amp;` (and
 * `<` as `&lt;`), which core.pdf decodes when it prints; this is the browser's half of that
 * contract. Without it the preview showed the text "&amp;", and the template editor — which is
 * built from this same renderer — loaded "&amp;" as typed text and escaped it again on save, one
 * extra layer per save. Applied to text runs only, *after* tags are recognised, so an escaped
 * `&lt;b&gt;` shows as the characters "<b>" and never becomes bold.
 */
const _NAMED = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: String.fromCharCode(160) };
export const decodeEntities = (s) =>
  String(s).replace(/&(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-fA-F]+);/g, (m, e) => {
    if (e[0] !== "#") return _NAMED[e];
    const code = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    try { return String.fromCodePoint(code); } catch { return m; }
  });

export const renderTemplateBody = (body, data) =>
  String(body || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const v = data?.[key];
    if (v === undefined || v === null || !String(v).trim()) return BLANK;
    return `<b>${_escapeForInlineMarkup(formatDocDate(v))}</b>`;
  });

// ---------------------------------------------------------------------------------------------
// A deliberately small allow-list of markup a template's `body_markdown` (or the admin's per-case
// override body) may carry — <h1>-<h6>, <b>/<strong>, <i>/<em>, <u>, <br>, <font size="1"-"7">.
// Mirrors the server's `core.pdf._html_lite_to_rl`/`_split_blocks`, so the wizard's live preview,
// the customer's view and the admin's document viewer all read the same document the printed PDF
// will contain.
//
// **Deliberately not `dangerouslySetInnerHTML`.** That would trust the entire string as HTML, and
// `body_markdown` is admin-authored content shown to a *different* person (the customer) — the one
// write path for it (`sanitize_template_body`, server-side) only strips `<script>`, not an
// event-handler attribute smuggled onto some other tag. `renderInline` below parses the allow-list
// itself and builds real React elements from it, so anything outside that allow-list — a stray
// tag, an attribute, a typo — comes out as inert, visible text, never something the browser runs.
const _HEADING_RE = /^<\s*h([1-6])([^>]*)>([\s\S]*?)<\s*\/\s*h\1\s*>$/i;
const _PARA_ALIGN_RE = /^<\s*p([^>]*)>([\s\S]*?)<\s*\/\s*p\s*>$/i;
const _ALIGN_ATTR_RE = /align\s*=\s*"?(left|center|right)"?/i;

/** `align="left|center|right"` out of a tag's attribute string, or undefined — the Word-like
 *  Align Left/Centre/Right toolbar buttons write exactly this attribute. Mirrors core.pdf's
 *  `_find_align`. */
const findAlign = (attrs) => _ALIGN_ATTR_RE.exec(attrs || "")?.[1]?.toLowerCase();

/** Drop whole blank lines from the start/end of `text`, but never touch real content — leading
 *  indentation on the first real line ("    a. ...") must survive. A bare `.trim()` doesn't know
 *  the difference, which is exactly how a `<p align="...">`-wrapped paragraph's own indentation
 *  went missing the first time this was written: `m[2].trim()` stripped the *captured inner text
 *  itself*, not just blank lines around it. Mirrors core.pdf._trim_blank_edges. */
const trimBlankEdges = (text) => {
  const lines = String(text || "").split("\n");
  while (lines.length && !lines[0].trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  return lines.join("\n");
};

/** A body into `{kind, text, align}` blocks on blank lines — `"h1"`-`"h6"` for a paragraph that
 *  is nothing but a single `<hN>...</hN>`, `"p"` otherwise. `align` is `undefined` (the kind's
 *  own default) unless the wrapping tag carries `align="..."`. Mirrors `core.pdf._split_blocks`.
 *
 *  A paragraph's own formatting survives — indentation before a lettered sub-clause, a single
 *  line break inside it — the block's `whiteSpace: "pre-wrap"` style (see BODY_CSS) renders it
 *  as typed. Only whole blank lines at a block's own edges are dropped; trimming the block's
 *  *content* down with them is what used to eat typed-in indentation. */
export const splitBlocks = (text) =>
  String(text || "").trim().split(/\n\s*\n/).filter((p) => p.trim()).map((para) => {
    const stripped = para.trim();
    let m = _HEADING_RE.exec(stripped);
    if (m) return { kind: `h${m[1]}`, text: trimBlankEdges(m[3]), align: findAlign(m[2]) };
    m = _PARA_ALIGN_RE.exec(stripped);
    if (m) return { kind: "p", text: trimBlankEdges(m[2]), align: findAlign(m[1]) };
    return { kind: "p", text: trimBlankEdges(para) };
  });

// Matches whichever recognised **opening** tag comes first: <b>/<i>/<u>, <font size="N">, <br>.
// `renderInline` below walks a block's text with this, extracting each match's own balanced
// closing tag (first-match, no same-tag nesting — nothing in this allow-list is ever written
// nested inside itself) and recursing on the inside, so `<font size="5"><b>...</b></font>` — the
// real HTML way to ask for "bold, and bigger" together — nests correctly either way round.
const _OPEN_RE = /<(b|i|u)>|<font\s+size\s*=\s*"?(\d+)"?\s*>|<br\s*\/?>/gi;

function _renderRun(text, keyBox) {
  const nodes = [];
  let i = 0;
  while (i < text.length) {
    _OPEN_RE.lastIndex = i;
    const m = _OPEN_RE.exec(text);
    if (!m) { nodes.push(decodeEntities(text.slice(i))); break; }
    if (m.index > i) nodes.push(decodeEntities(text.slice(i, m.index)));
    if (/^<br/i.test(m[0])) { nodes.push("\n"); i = m.index + m[0].length; continue; }
    const isFont = m[2] !== undefined;
    const tagName = isFont ? "font" : m[1].toLowerCase();
    const afterOpen = m.index + m[0].length;
    const closeM = new RegExp(`<\\s*/\\s*${tagName}\\s*>`, "i").exec(text.slice(afterOpen));
    const innerEnd = closeM ? afterOpen + closeM.index : text.length;
    const children = _renderRun(text.slice(afterOpen, innerEnd), keyBox);
    if (isFont) {
      const level = Math.max(1, Math.min(7, parseInt(m[2], 10)));
      nodes.push(<span key={keyBox.k++} style={{ fontSize: `${FONT_SIZE_RATIO[level]}em` }}>{children}</span>);
    } else {
      const Tag = tagName;
      nodes.push(<Tag key={keyBox.k++}>{children}</Tag>);
    }
    i = closeM ? innerEnd + closeM[0].length : text.length;
  }
  return nodes;
}

/** One block's text -> an array of React children, honouring only the allow-list above (after
 *  normalising <strong>/<em> onto <b>/<i>) — never raw HTML. */
export const renderInline = (text) => {
  const norm = String(text || "")
    .replace(/<\s*strong\s*>/gi, "<b>").replace(/<\s*\/\s*strong\s*>/gi, "</b>")
    .replace(/<\s*em\s*>/gi, "<i>").replace(/<\s*\/\s*em\s*>/gi, "</i>");
  return _renderRun(norm, { k: 0 });
};
